import { SB } from '@play-co/replicant';
import { getPetAssetKey, speciesAssetsRatio, speciesChatbot } from './species';
import {
  createFlexMessageTemplate,
  createFlexContainer,
  createOpenGameAction,
} from './utils';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import { buildSender } from '../sender';

/**
 * Assets for greeting messages
 */
export const greetingAssets = {
  'background/greeting/morning':
    'private/chatbot/greeting/greeting_morning.png',
  'background/greeting/lunch': 'private/chatbot/greeting/greeting_lunch.png',
  'background/greeting/evening':
    'private/chatbot/greeting/greeting_evening.png',
};

/**
 * Create chatbot message
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const greetingMessage = createFlexMessageTemplate(
  SB.object({
    message: SB.string(),
    species: SB.tuple(speciesChatbot),
    petName: SB.string(),
    locale: SB.tuple(['en', 'ja']),
  }),
  (data, payload, api) => {
    const normal = api.getAssetPath(getPetAssetKey(data.species));
    const cta = getLocaleConfig(data.locale).resurrect.playNow.text;

    const template = createFlexContainer(data.message, {
      type: 'bubble',
      body: {
        type: 'box',
        layout: 'baseline',
        contents: [
          {
            type: 'text',
            text: data.message,
            wrap: true,
          },
        ],
      },
      footer: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'button',
            style: 'primary',
            height: 'sm',
            action: createOpenGameAction(cta, payload),
          },
        ],
      },
    });

    // Add sender to message
    template['sender'] = buildSender({ name: data.petName, iconUrl: normal });

    return template;
  },
);

export const greetingImageMessage = createFlexMessageTemplate(
  SB.object({
    message: SB.string(),
    species: SB.tuple(speciesChatbot),
    petName: SB.string(),
    locale: SB.tuple(['en', 'ja']),
    period: SB.tuple(['morning', 'lunch', 'evening'] as const),
  }),
  (data, payload, api) => {
    const normal = api.getAssetPath(getPetAssetKey(data.species));
    const happyKey = getPetAssetKey(data.species, 'happy');

    const textColors = {
      morning: '#6690AE',
      lunch: '#A67E61',
      evening: '#6179A6',
    };

    const cta = getLocaleConfig(data.locale).resurrect.playNow.text;

    const template = createFlexContainer(data.message, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(cta, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath(`background/greeting/${data.period}`),
            size: 'full',
            aspectMode: 'cover',
            aspectRatio: '270:179',
            gravity: 'center',
          },
          {
            type: 'box',
            layout: 'horizontal',
            contents: [
              {
                type: 'text',
                text: data.message,
                color: textColors[data.period],
                wrap: true,
                lineSpacing: '0px',
                align: 'center',
                gravity: 'center',
                weight: 'bold',
                size: 'md',
              },
            ],
            position: 'absolute',
            offsetStart: '0px',
            offsetEnd: '0px',
            offsetTop: '0px',
            width: '100%',
            height: '43%',
            paddingAll: '20px',
          },
          {
            type: 'image',
            url: api.getAssetPath(happyKey),
            position: 'absolute',
            aspectRatio: speciesAssetsRatio[happyKey],
            aspectMode: 'fit',
            offsetBottom: '17px',
            offsetStart: '37%',
          },
        ],
        paddingAll: '0px',
      },
    });

    // Add sender to message
    template['sender'] = buildSender({ name: data.petName, iconUrl: normal });

    return template;
  },
);
